/**
 * Display preferences: theme (dark default, ADR-0007), density, the nav's
 * collapsed state and the interface mode (skin: Velora Modern, Dentrix /
 * Open Dental / Eaglesoft Familiar) with its own light/dark choice. They
 * follow the user across devices (server preferences) and are cached on
 * this device so the first paint is already right. Applied through the
 * skin runtime and @velora/ui's AppearanceProvider.
 */
import {
  HomeLayoutSchema,
  NavLayoutSchema,
  ToolbarLayoutsSchema,
  type HomeLayout,
  type NavLayout,
  type ScheduleColorBy,
  type ShellPreferences,
  type TableSorts,
  TableSortsSchema,
  type ToolbarLayouts,
} from '@velora/contracts/shell';
import {
  isSkinId,
  resolveSkin,
  SKIN_DEFAULT_MODE,
  SkinIdSchema,
  SkinOverridesSchema,
  type AppearanceMode,
  type SkinId,
  type SkinOverrides,
} from '@velora/contracts/skins';
import { putShellPreferences } from '@velora/sdk';
import { AppearanceProvider } from '@velora/ui';
import type { Appearance, Density, Theme } from '@velora/tokens';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { api } from '../api.js';
import { readJson, writeJson } from '../lib/storage.js';
import { SkinProvider, useSkin, useSkinValue } from '../skins/SkinProvider.js';

const KEY = 'velora.prefs';

export type SkinModes = Partial<Record<SkinId, AppearanceMode>>;

export interface DisplayPrefs {
  /** Velora Modern's light/dark (the other modes keep theirs in `skinModes`). */
  theme: Theme;
  density: Density;
  navCollapsed: boolean;
  /** Device only: no blur or animated backgrounds (weak front-desk PCs). */
  performance: boolean;
  /** The employee's own mode; undefined = not chosen (practice default or office PMS). */
  skin: SkinId | undefined;
  skinOverrides: SkinOverrides;
  skinModes: SkinModes;
  skinOnboarded: boolean;
  /** The user's customized classic toolbars, per mode and toolbar. */
  toolbarLayouts: ToolbarLayouts;
  /** Velora Modern side nav: pins, hidden items, collapsed categories. */
  navLayout: NavLayout;
  /** Home: pinned cards and the role layout it starts from. */
  homeLayout: HomeLayout;
  /** Velora Modern schedule: fill cards by provider (default) or appointment type. */
  scheduleColorBy: ScheduleColorBy;
  /** Velora Modern schedule: "My schedule"; undefined = the role default. */
  scheduleMine: boolean | undefined;
  /** Column sorts of the big tables, per user (`useTableSort`). */
  tableSorts: TableSorts;
}

export const DEFAULT_PREFS: DisplayPrefs = {
  theme: 'dark',
  density: 'compact',
  navCollapsed: false,
  performance: false,
  skin: undefined,
  skinOverrides: {},
  skinModes: {},
  skinOnboarded: false,
  toolbarLayouts: {},
  navLayout: {},
  homeLayout: {},
  scheduleColorBy: 'provider',
  scheduleMine: undefined,
  tableSorts: {},
};

/** What the practice and office imply when the employee has not chosen. */
export interface SkinContextInput {
  practiceDefault: SkinId | null;
  officePms: string | null;
}

interface PrefsValue extends DisplayPrefs {
  set: (patch: Partial<DisplayPrefs>) => void;
  /** Takes the signed-in user's saved preferences (once per session). */
  adopt: (server: ShellPreferences, signedIn: boolean) => void;
  /** The mode in effect (choice, else practice default, else office PMS). */
  effectiveSkin: SkinId;
  /** Light or dark for the mode in effect. */
  mode: AppearanceMode;
  /** Sets light or dark for the mode in effect. */
  setMode: (mode: AppearanceMode) => void;
  setSkinContext: (ctx: SkinContextInput) => void;
  skinContext: SkinContextInput;
  /** The signed-in user's server preferences have been read this session. */
  serverAdopted: boolean;
}

const PrefsContext = createContext<PrefsValue | null>(null);

function modesFrom(raw: unknown): SkinModes {
  if (typeof raw !== 'object' || raw === null) return {};
  const out: SkinModes = {};
  for (const [k, v] of Object.entries(raw)) {
    if (isSkinId(k) && (v === 'light' || v === 'dark')) out[k] = v;
  }
  return out;
}

function loadLocal(): DisplayPrefs {
  const raw = readJson<Partial<Record<keyof DisplayPrefs, unknown>>>(KEY, {});
  const overrides = SkinOverridesSchema.safeParse(raw.skinOverrides);
  return {
    theme: raw.theme === 'light' ? 'light' : 'dark',
    density:
      raw.density === 'comfortable' || raw.density === 'classic'
        ? raw.density
        : DEFAULT_PREFS.density,
    navCollapsed: raw.navCollapsed === true,
    performance: raw.performance === true,
    skin: isSkinId(raw.skin) ? raw.skin : undefined,
    skinOverrides: overrides.success ? overrides.data : {},
    skinModes: modesFrom(raw.skinModes),
    skinOnboarded: raw.skinOnboarded === true,
    toolbarLayouts: layoutsFrom(raw.toolbarLayouts),
    navLayout: navLayoutFrom(raw.navLayout),
    homeLayout: homeLayoutFrom(raw.homeLayout),
    scheduleColorBy: raw.scheduleColorBy === 'type' ? 'type' : 'provider',
    scheduleMine: typeof raw.scheduleMine === 'boolean' ? raw.scheduleMine : undefined,
    tableSorts: tableSortsFrom(raw.tableSorts),
  };
}

function tableSortsFrom(raw: unknown): TableSorts {
  const parsed = TableSortsSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

function navLayoutFrom(raw: unknown): NavLayout {
  const parsed = NavLayoutSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

function homeLayoutFrom(raw: unknown): HomeLayout {
  const parsed = HomeLayoutSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

function layoutsFrom(raw: unknown): ToolbarLayouts {
  const parsed = ToolbarLayoutsSchema.safeParse(raw);
  return parsed.success ? parsed.data : {};
}

/** Light or dark for `skin`: Velora uses `theme`, the others their own choice or light. */
export function modeFor(prefs: Pick<DisplayPrefs, 'theme' | 'skinModes'>, skin: SkinId) {
  if (skin === 'velora') return prefs.theme;
  return prefs.skinModes[skin] ?? SKIN_DEFAULT_MODE[skin];
}

/** The server patch for a local change: device-only fields stay local; undefined clears. */
function serverPatch(patch: Partial<DisplayPrefs>): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  for (const [k, v] of Object.entries(patch)) {
    if (k === 'performance') continue;
    out[k] = v === undefined ? null : v;
  }
  return out;
}

export function PrefsProvider({ children }: { children: ReactNode }) {
  const [prefs, setPrefs] = useState<DisplayPrefs>(loadLocal);
  const [skinContext, setSkinContextState] = useState<SkinContextInput>({
    practiceDefault: null,
    officePms: null,
  });
  const signedIn = useRef(false);
  const adopted = useRef(false);
  const [serverAdopted, setServerAdopted] = useState(false);

  const set = useCallback((patch: Partial<DisplayPrefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...patch };
      writeJson(KEY, next);
      return next;
    });
    if (signedIn.current) {
      const synced = serverPatch(patch);
      if (Object.keys(synced).length > 0) {
        putShellPreferences(api, { body: synced }).catch(() => {
          // Kept on this device; the server copy catches up on the next change.
        });
      }
    }
  }, []);

  const adopt = useCallback((server: ShellPreferences, isSignedIn: boolean) => {
    signedIn.current = isSignedIn;
    if (adopted.current) return;
    adopted.current = true;
    setServerAdopted(isSignedIn);
    setPrefs((prev) => {
      const skin = SkinIdSchema.safeParse(server.skin);
      const next: DisplayPrefs = {
        ...prev,
        ...(server.theme ? { theme: server.theme } : {}),
        ...(server.density ? { density: server.density } : {}),
        ...(server.navCollapsed !== undefined ? { navCollapsed: server.navCollapsed } : {}),
        // The server is the source of truth for the mode: a choice cleared on
        // another device clears here too.
        skin: skin.success ? skin.data : undefined,
        skinOverrides: server.skinOverrides ?? {},
        skinModes: modesFrom(server.skinModes),
        skinOnboarded: server.skinOnboarded === true,
        toolbarLayouts: layoutsFrom(server.toolbarLayouts),
        navLayout: navLayoutFrom(server.navLayout),
        homeLayout: homeLayoutFrom(server.homeLayout),
        ...(server.scheduleColorBy ? { scheduleColorBy: server.scheduleColorBy } : {}),
        scheduleMine: server.scheduleMine,
        tableSorts: tableSortsFrom(server.tableSorts),
      };
      writeJson(KEY, next);
      return next;
    });
  }, []);

  // Another window of this browser (a pop-out, a second tab) changed the
  // mode or light/dark: follow it here too, so the appearance switch and
  // Settings > Appearance act on every open Velora window at once.
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key !== KEY || e.newValue === null) return;
      const other = loadLocal();
      setPrefs((prev) =>
        prev.skin === other.skin &&
        prev.theme === other.theme &&
        JSON.stringify(prev.skinModes) === JSON.stringify(other.skinModes) &&
        JSON.stringify(prev.skinOverrides) === JSON.stringify(other.skinOverrides)
          ? prev
          : {
              ...prev,
              skin: other.skin,
              theme: other.theme,
              skinModes: other.skinModes,
              skinOverrides: other.skinOverrides,
              skinOnboarded: prev.skinOnboarded || other.skinOnboarded,
            },
      );
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const setSkinContext = useCallback((ctx: SkinContextInput) => {
    setSkinContextState((prev) =>
      prev.practiceDefault === ctx.practiceDefault && prev.officePms === ctx.officePms ? prev : ctx,
    );
  }, []);

  const effectiveSkin = resolveSkin({
    userSkin: prefs.skin,
    practiceDefault: skinContext.practiceDefault,
    officePms: skinContext.officePms,
  });
  const mode = modeFor(prefs, effectiveSkin);

  const setMode = useCallback(
    (m: AppearanceMode) => {
      if (effectiveSkin === 'velora') set({ theme: m });
      else set({ skinModes: { ...prefs.skinModes, [effectiveSkin]: m } });
    },
    [effectiveSkin, prefs.skinModes, set],
  );

  const value = useMemo(
    () => ({
      ...prefs,
      set,
      adopt,
      effectiveSkin,
      mode,
      setMode,
      setSkinContext,
      skinContext,
      serverAdopted,
    }),
    [prefs, set, adopt, effectiveSkin, mode, setMode, setSkinContext, skinContext, serverAdopted],
  );

  const skinValue = useSkinValue({
    skin: effectiveSkin,
    overrides: prefs.skinOverrides,
    mode,
  });

  return (
    <PrefsContext.Provider value={value}>
      <SkinProvider value={skinValue}>
        <SkinAppearance density={prefs.density} performance={prefs.performance}>
          {children}
        </SkinAppearance>
      </SkinProvider>
    </PrefsContext.Provider>
  );
}

/** Writes data-theme, data-skin, data-density and data-clin from the active mode. */
function SkinAppearance({
  density,
  performance,
  children,
}: {
  density: Density;
  performance: boolean;
  children: ReactNode;
}) {
  const skin = useSkin();
  const appearance = useMemo<Appearance>(
    () => ({
      theme: skin.mode,
      skin: skin.layers.tokens,
      contrast: 'standard',
      density: skin.density ?? density,
      clinical: skin.clinicalPreset,
      reducedMotion: false,
      performance,
    }),
    [skin.mode, skin.layers.tokens, skin.density, skin.clinicalPreset, density, performance],
  );
  return <AppearanceProvider appearance={appearance}>{children}</AppearanceProvider>;
}

/** The preferences, or null outside a `PrefsProvider` (isolated component tests, previews). */
export function usePrefsOptional(): PrefsValue | null {
  return useContext(PrefsContext);
}

export function usePrefs(): PrefsValue {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error('usePrefs must be used inside <PrefsProvider>');
  return ctx;
}
